import React, { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, AlertCircle } from "lucide-react";
import { Button } from "@/shared/ui/core";

type ConfirmVariant = "danger" | "warning" | "info";

export interface ConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
}

const variantConfig: Record<
  ConfirmVariant,
  { icon: React.ReactNode; iconBg: string; buttonVariant: "danger" | "primary" }
> = {
  danger: {
    icon: <AlertCircle className="h-5 w-5 text-[#ff3b30]" />,
    iconBg: "bg-[#ff3b30]/10",
    buttonVariant: "danger",
  },
  warning: {
    icon: <AlertTriangle className="h-5 w-5 text-[#ff9f0a]" />,
    iconBg: "bg-[#ff9f0a]/10",
    buttonVariant: "danger",
  },
  info: {
    icon: <Info className="h-5 w-5 text-[#0071e3]" />,
    iconBg: "bg-[#0071e3]/10",
    buttonVariant: "primary",
  },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  variant = "danger",
  loading = false,
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const config = variantConfig[variant];

  /* focus trap + escape */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      if (e.key !== "Tab" || !contentRef.current) return;

      const focusable = contentRef.current.querySelectorAll<HTMLElement>(
        "button:not([disabled])",
      );
      if (focusable.length === 0) return;

      // `noUncheckedIndexedAccess`: el acceso por índice puede ser undefined.
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onCancel],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    const timer = setTimeout(() => {
      contentRef.current
        ?.querySelector<HTMLElement>("button:not([disabled])")
        ?.focus();
    }, 0);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(timer);
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-sm p-4 transition-opacity"
      aria-modal="true"
      role="alertdialog"
      onClick={(e) => {
        if (e.target === overlayRef.current) onCancel();
      }}
    >
      <div
        ref={contentRef}
        className="w-full max-w-[400px] rounded-2xl bg-ds-surface backdrop-blur-3xl shadow-glass-lg border border-ds-border p-6 animate-[apple-fade-in_0.25s_cubic-bezier(0.25,0.1,0.25,1)]"
      >
        <div className="flex gap-4">
          <div
            className={[
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
              config.iconBg,
            ].join(" ")}
          >
            {config.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-[15px] font-semibold text-[#1d1d1f]">{title}</h3>
            <p className="mt-1 text-[13px] text-[#86868b] leading-relaxed">{description}</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={loading}>
            {t(cancelLabel)}
          </Button>
          <Button
            variant={config.buttonVariant}
            size="sm"
            onClick={onConfirm}
            loading={loading}
          >
            {t(confirmLabel)}
          </Button>
        </div>
      </div>
    </div>
  );
};
